import moment from 'moment-timezone';

export const DERTypes = {
  DENSTORE: 'DEN-STORE',
  SOLAR: 'SOLAR',
  FUELCELL: 'FUEL-CELL'
};

export const DERStatus = {
  CHARGING: 'CHARGING',
  GENERATING: 'GENERATING',
  CHARGE: 'CHARGE',
  LINE_CHARGE: 'LINE_CHARGE',
  GENERATE: 'GENERATE',
  IDLE: 'IDLE',
  VARIED: 'VARIED'
};

export const SiteStatus = {
  BULK_CHARGE: 'BulkCharge',
  BULK_GENERATE: 'BulkGenerate',
  MANUAL_DEMAND_CAP_GENERATE: 'ManualDemandCapGenerate',
  AUTOMATIC_DEMAND_CAP: 'AutomaticDemandCap',
  AUTOMATIC_OPTIMIZATION: 'AutomaticOptimization',
  IDLE: 'Idle'
};


export interface DeviceState {
  status: string;
  available: boolean;
  howLongUnavailable: number;
  stateOfCharge: number;
  power: number;
}

export interface BatteryRack {
  name: string;
  id: string;
  siteId: string;
  numBatteries: number;
  rackNum?: number;
  rackNumber: number;
}

export interface DER {
  name: string;
  id: string;
  sID: string;
  type: string;
  siteId: string;
  batterySystem: string;
  availablePower: number;
  maxDOD: number;
  racks: BatteryRack [];
  nameplateBatteryEnergy: number;
  state: DeviceState;
  config: [];
}

export interface Site {
  name: string;
  id: string;
  timezone: string;
  sID: string;
  address: string;
  state: DeviceState;
  deviceId: string;
  billingPeriods: BillingPeriod[];
  rates: SiteRates;
  utility: string;
  latitude: string;
  longitude: string;
  postalCode: string;
  vppSiteConfigurationFactor: number;
  tags: string;
  stores: DER[];
  availablePower: number;
  realTimePower: number;
  commissioned: boolean;
  ftm: boolean;
  account: string;
}

export interface Rate {
  key: string;
  label: string;
}

export interface Tariff {
  code: string;
  name: string;
}

export interface UtilityTariffs {
  supply: Tariff[];
  delivery: Tariff[];
}

export interface SiteRates {
  deliveryRateAfter: {
    key: any,
    label: string
  };
  deliveryRateBefore: {
    key: any,
    label: string
  };
  supplyRateAfter: {
    key: any,
    label: string
  };
  supplyRateBefore: {
    key: any,
    label: string
  };
}

export interface BillingPeriod {
  month: number;
  year: number;
  day: number;
}

export interface BillingConfiguration {
  solarPPA: any;
  supplyCharge: any;
  contractDemand: any;
}

export interface Utility {
  name: string;
  id: string;
  shortName: string;
  lseId: string;
}

export interface SiteTariffs {
  supplyBefore: string;
  supplyAfter: string;
  deliveryBefore: string;
  deliveryAfter: string;
  supplyCharge: number;
}

export interface SiteCharges {
  solarPPA: number;
  contractDemand: number;
}
export interface LegacyUtility {
  key: string;
  label: string;
}


export interface BillingPeriods {
  billingPeriods: BillingPeriod[];
}

export interface Account {
  name: string;
  id: string;
  sites: Site[];
}

export interface Developer {
  name: string;
  id: string;
  accounts: Account [];
}

export interface Program {
  enabled?: true,
  forecastServiceName?: string,
  geolocations?: string[],
  id: string,
  name: string,
  nameCode?: string;
  programType: string,
  properties: {
    programPenalty: number,
    performanceTarget: string,
    marketAuction: boolean,
    notificationTimeframe: number,
    programIncentive: number,
    pointOfMeasurement: string,
    nominatedKw: number,
    forwardStats: boolean,
    forceMaxDispatch: boolean,
    gridCode?: string
  },
  siteIncentivesAndPenalties?:  {},
  sites?: string[]
}

export interface ProgramDialogData {
  program: Program;
}

export interface Dispatch {
  setpoint: number;
  duration: string;
  incentive: number;
  penalty: number;
  id: string;
  priority?: number;
  targetId?: string;
  status?: string;
  type?: string;
  startTimeUtc?: string;
  createdTimeUtc?: string;
  endTimeUtc?: string;
  predictedOutput?: number | string;
  plannedPower?: number;
}

export interface DispatchDialogData {
  dispatch: Dispatch;
  program?: Program;
  programs?: Program[];
  timezone?: string;
}

export interface Store {
  id?: string;
  name: string;
  sid?: string;
  siteId?: string;
}

export interface Device {
  store: Store;
  racks?: BatteryRack[];
  config: {
    'available-power'?: number,
    'battery-system'?: number,
    'max-dod'?: number,
    'max-soc'?: number,
    'min-soc'?: number,
    'nameplate-energy'?: number,
    numRacks?: number,
    'state-of-health'?: number,
    type: string,
    'usable-energy'?: number
  }
}

export class UIDispatch {
  static convertToUtc(dispatch: Dispatch): Dispatch {
    dispatch.startTimeUtc = moment(dispatch.startTimeUtc).utc().format();
    dispatch.endTimeUtc = moment(dispatch.endTimeUtc).utc().format();
    return dispatch;
  }

  static convertFromUtc(dispatch: Dispatch, timezone?: string): Dispatch {
    if (timezone) {
      let startTime = moment(dispatch.startTimeUtc + 'Z').tz(timezone);
      let endTime = moment(dispatch.endTimeUtc + 'Z').tz(timezone);

      dispatch.startTimeUtc = startTime.format();
      dispatch.endTimeUtc = endTime.format();
    }

    return dispatch;
  }

  static cleanNumericOutput(dispatchValue): string {
    if (dispatchValue) {
      return ''+dispatchValue.toFixed(1)
        .replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/,'$1')
        .replace(',', '').toString();
    } else {
      return '0';
    }
  }
}

export class Programs {
  static sortAlphabetically(programs: Program[]): Program[] {
    return programs.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }

      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }

      return 0;
    });
  }

  static getIncentiveAndPenalty(program: Program, siteId: string): ProgramIncentivePenalty {
    let ret: ProgramIncentivePenalty;
    if (program.siteIncentivesAndPenalties) {
      Object.entries(program.siteIncentivesAndPenalties).forEach(([key, element]) => {
        // @ts-ignore
        if( key == siteId && element) {
         return {
           incentive: element['incentive'],
           penalty: element['penalty']
         }
        }
      });
    }

    return  {
      incentive: null,
      penalty: null,
    }
  }
}

export interface ProgramIncentivePenalty {
  incentive: number;
  penalty: number;
}
export interface ProgramSite {
  id: string;
  name: string;
  timezone: string;
  latitude: string;
  longitude: string;
}

export interface MapLocations {
  lat: string;
  long: string;
  description?: string;
}

export interface PowerValue {
  defaultUnit: string;
  value: number;
}

export interface ServiceVersions {
  build: VersionInfo;
  services: VersionInfo[];
}

export interface PercentValue {
  defaultUnit: string;
  value: number;
}
export interface VersionInfo {
  version: string;
  name: string;
}

export interface DateRange {
  start: string;
  stop: string;
}

export interface KeyVarElement {
  key: string;
  value: string;
}

export interface TopologyObject {
  id: string;
  parentId: string;
  name: string;
}

export interface SiteTopology {
  id: string,
  name: string,
  accountId: string,
  accountName: string,
  developerId: string,
  developerName: string,
  programs?: Program[]
}

export interface SearchItem {
  id: string;
  name: string;
  type: string;
}

export interface SchedulingParams {
  developers?: string[];
  accounts?: string[];
  sites?: string[];
  programs?: string[];
  systemTypes?: string[];
  activityTypes?: string[];
  timezone: string;
  startDate?: string;
  stopDate?: string;
  activityStatuses?: string[];
}

export interface SiteSchedules {
  siteActivities: SiteActivity[]
}

export interface SiteActivity {
  siteId: string;
  activities: Activity[];
}

export interface Activity {
  id: string;
  createdTimeUtc: string;
  startTimeUtc: string;
  endTimeUtc: string;
  lastUpdatedTimeUtc: string;
  duration: string;
  incentive: string;
  penalty: number;
  priority: number;
  properties: Map<string, string>;
  setpoint: number;
  status: string;
  targetId: string;
  type: string;
  enabled: boolean;
}

export interface ChartSeriesElement {
  name: string;
  data: any[];
}

export interface SiteDispatchStats {
  maxDispatchPower: PowerValue;
  plannedPower: PowerValue;
  requestedPower: PowerValue;
  currentPower: PowerValue;
  siteId: string;
  siteStatus: string;
  siteSystemState: string;
  derStates: string[];
  statusDetail: string;
}

export interface DispatchStats {
  maxDispatchPower: PowerValue;
  plannedPower: PowerValue;
  requestedPower: PowerValue;
  currentPower: PowerValue;
  stateOfCharge: PercentValue;
  siteDispatchStatuses:SiteDispatchStats[];
  status: string;
  statusDetail: string;
}


export interface ZonedDispatch extends Dispatch {
  shortZone: string;
  dispatchStats?: DispatchStats;
  maxDispatchPower?: number;
  requestedPower?: number;
  sites?: Site[];
}

